import { useMutation } from '@apollo/client'
import { FormModal } from '@chaine/keychaine'
import React from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { AppType } from '../../../_shared/types/app-types'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_GREENSCREENS_APP, SaveClientIDAndSecretInput } from '../data/mutations'

/**
 * Save's Greenscreens app's client id and client secret for enable this app
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const GreenscreensModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props

  const { showToast } = Toast()

  const [enableGreenscreensApp, { loading }] = useMutation<
    { enableGreenscreensApp: { id: string } },
    SaveClientIDAndSecretInput
  >(ENABLE_GREENSCREENS_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `Greenscreens app has been enabled`
      })
      refetchApps()
      onClose()
    },

    onError: (err) => {
      if (err.message === 'InvalidClient') {
        showToast({
          status: 'error',
          title: `Invalid Greenscreens client id or client secret. Please check the values and try again.`
        })
      } else {
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  type FieldValues = { clientID: string; clientSecret: string }

  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableGreenscreensApp({
      variables: { appType: AppType.Greenscreens, clientID: event.clientID, clientSecret: event.clientSecret }
    })
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Enable ${appType} for Chaine`}
      subtitle={`Enabling this integration requires a Greenscreens.ai account. You will need to add your Greenscreens "client id" and "client secret" to enable this app. If you don't have this available, ask your Greenscreens account manager for this information or email support@greenscreens.ai.`}
      formSubmitHandler={handleSaveDetails}
      buttonText={'Save consumer details'}
      initialValues={{}}
      validations={validations}
      isLoading={loading}
      modalItems={[
        {
          labelText: 'Client id',
          name: 'clientID',

          placeHolder: 'Enter your greenscreens client id',
          type: 'input'
        },
        {
          labelText: 'Client secret',
          name: 'clientSecret',

          placeHolder: 'Enter the client secret',
          type: 'input'
        }
      ]}
    />
  )
})

GreenscreensModal.displayName = 'GreenscreensModal'

export { GreenscreensModal }
